<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Promotions</template>
            <template v-slot:pageDetail>Settings</template>
        </o-bread-crumb>

        <FeatureGate featureKey="promotions_module">
            <div class="row promotions-container" v-if="thisComponentShow">
            <!-- Left Side: Settings & Management -->
            <div class="col-lg-7">
                
                <!-- Main Card -->
                <div class="promotions-card" v-if="page.output && hasSettings && !showCreateForm">
                    <!-- Header -->
                    <div class="promotions-header">
                        <div class="header-left">
                            <h4 class="header-title">
                                <i class="fas fa-tags me-2 primary-text"></i>Promotion Settings
                            </h4>
                            <p class="header-subtitle">Configure promotions displayed on members</p>
                        </div>
                        <div class="header-actions" v-if="write">
                            <div v-if="!isEditingSettings">
                                <button class="btn-primary-action" @click="startEditSettings">
                                    <i class="fas fa-edit me-2"></i>Edit
                                </button>
                            </div>
                            <div v-else class="d-flex gap-2">
                                <button class="btn-primary-action" @click="saveSettings" :disabled="page.isSaving || !removalStats.isValid">
                                    <span v-if="page.isSaving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="fas fa-check me-2"></i>
                                    {{ page.isSaving ? 'Saving...' : 'Save Changes' }}
                                </button>
                                <button class="btn-secondary-action" @click="cancelEditSettings">
                                    <i class="fas fa-times me-2"></i>Cancel
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="promotions-content">
                        <!-- Edit Mode Warning -->
                        <div v-if="isEditingSettings && removalStats.needToRemove > 0" class="alert-warning-custom mb-4">
                            <div class="d-flex align-items-center">
                                <div class="warning-icon-circle me-3">
                                    <i class="fas fa-exclamation-triangle"></i>
                                </div>
                                <div>
                                    <strong class="text-dark">Action Required</strong>
                                    <div class="small text-muted mt-1">
                                        Please select {{ removalStats.needToRemove }} item(s) to remove (Click items below).
                                    </div>
                                </div>
                            </div>
                        </div>

                        <toast-notification 
                            v-model="toast.show" 
                            :type="toast.type" 
                            :message="toast.message" 
                            :title="toast.title" 
                        />



                        <!-- Settings Section -->
                        <div class="settings-section">
                            <div class="row">
                                <div class="col-md-4 mb-3 mb-md-0">
                                    <label class="section-label">Section Title</label>
                                    <div v-if="!isEditingSettings">
                                        <p class="section-value mb-0">{{ currentSetting?.section_title || 'Limited offers' }}</p>
                                    </div>
                                    <div v-else>
                                        <input 
                                            type="text" 
                                            class="form-control"
                                            v-model="editableSettings.section_title"
                                            placeholder="e.g., Limited offers"
                                            maxlength="50"
                                        >
                                    </div>
                                </div>
                                <div class="col-md-4 mb-3 mb-md-0">
                                    <label class="section-label">Content Type</label>
                                    <div v-if="!isEditingSettings">
                                        <p class="mb-0">
                                            <span :class="getContentTypeBadgeClass(currentSetting?.content_type)">
                                                {{ formatContentType(currentSetting?.content_type) }}
                                            </span>
                                        </p>
                                    </div>
                                    <div v-else>
                                        <select 
                                            class="form-select" 
                                            v-model="editableSettings.content_type"
                                            :disabled="!canSelectVoucher"
                                        >
                                            <option value="BENEFIT" v-if="isBenefitModuleEnabled">Benefits Only</option>
                                            <option value="VOUCHER" :disabled="!canSelectVoucher">Vouchers Only</option>
                                            <option value="BOTH" :disabled="!canSelectVoucher" v-if="isBenefitModuleEnabled">Both (Mixed)</option>
                                        </select>
                                        <small v-if="!canSelectVoucher" class="text-info d-block mt-1">
                                            <i class="fas fa-info-circle me-1"></i>Only can display 'benefit' when point system mode is NONACTIVE
                                        </small>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <label class="section-label">Display Limit</label>
                                    <div v-if="!isEditingSettings">
                                        <p class="section-value mb-0">{{ currentSetting?.display_limit || 5 }} items</p>
                                    </div>
                                    <div v-else>
                                        <input 
                                            type="number" 
                                            class="form-control"
                                            v-model.number="editableSettings.display_limit"
                                            min="1"
                                            max="50"
                                            @keypress="onlyNumbers"
                                            @input="checkDisplayLimit"
                                        >
                                        <small class="text-muted">Max items to show (1-50)</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Items Section -->
                        <div>
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <label class="section-label mb-0">Active Items</label>
                                    <div class="small text-muted">
                                        {{ items.length }} selected
                                        <span v-if="isEditingSettings">
                                            → Final: {{ removalStats.finalCount }}
                                        </span>
                                    </div>
                                </div>
                                <div class="d-flex gap-2" v-if="write">
                                    <button 
                                        v-if="isOrderChanged && !isEditingSettings"
                                        class="btn-secondary-action border-primary text-primary"
                                        @click="saveOrder"
                                        :disabled="page.isSaving"
                                    >
                                        <i class="fas fa-save me-1"></i>Save Order
                                    </button>
                                    <button 
                                        v-if="isOrderChanged && !isEditingSettings"
                                        class="btn-secondary-action"
                                        @click="cancelOrderChange"
                                        :disabled="page.isSaving"
                                    >
                                        <i class="fas fa-undo me-1"></i>Reset
                                    </button>
                                    <button 
                                        class="btn-primary-action" 
                                        @click="handleOpenItemModal"
                                        :disabled="items.length >= (isEditingSettings ? (editableSettings.display_limit || 5) : (currentSetting?.display_limit || 5)) && (!isEditingSettings || removalStats.finalCount >= (editableSettings.display_limit || 5))"
                                        v-if="isEditingSettings || items.length < (currentSetting?.display_limit || 5)"
                                    >
                                        <i class="fas fa-plus me-1"></i>Add Items
                                    </button>
                                </div>
                            </div>

                            <div v-if="items.length === 0" class="text-center py-5 bg-light rounded border border-dashed">
                                <i class="fas fa-inbox fa-3x text-muted mb-3 opacity-50"></i>
                                <p class="text-muted fw-medium">No items added yet</p>
                                <button 
                                    class="btn-primary-action btn-sm mt-2 mx-auto" 
                                    @click="showItemModal = true"
                                    v-if="!isEditingSettings && write"
                                >
                                    Add your first item
                                </button>
                            </div>

                            <div v-else class="items-list-container">
                                <transition-group name="list" tag="div" class="items-list">
                                    <div 
                                        v-for="(item, index) in itemsWithRemovalStatus" 
                                        :key="item.id" 
                                        class="item-row"
                                        :class="{ 
                                            'item-will-remove': item.willBeRemoved,
                                            'item-dragging': draggedIndex === index,
                                            'item-can-toggle': isEditingSettings && 
                                                             !((editableSettings.content_type === 'VOUCHER' && item.item_type === 'benefit') ||
                                                               (editableSettings.content_type === 'BENEFIT' && item.item_type === 'voucher')),
                                            'item-shake': isEditingSettings && removalStats.needToRemove > 0 && !item.willBeRemoved &&
                                                        !((editableSettings.content_type === 'VOUCHER' && item.item_type === 'benefit') ||
                                                          (editableSettings.content_type === 'BENEFIT' && item.item_type === 'voucher'))
                                        }"
                                        :draggable="!item.willBeRemoved"
                                        @dragstart="dragStart(index, $event)"
                                        @dragend="dragEnd"
                                        @dragover.prevent="dragOver(index, $event)"
                                        @drop="drop"
                                        @click="toggleItemRemoval(item)"
                                    >
                                        <div class="item-drag-handle" v-if="!isEditingSettings">
                                            <i class="fas fa-grip-vertical"></i>
                                        </div>
                                        <div class="item-removal-indicator" v-else-if="item.willBeRemoved">
                                            <i class="fas fa-times-circle text-danger"></i>
                                        </div>
                                        <div class="item-keep-indicator" v-else-if="isEditingSettings">
                                            <i class="fas fa-check-circle text-success"></i>
                                        </div>
                                        
                                        <div class="item-order">{{ index + 1 }}</div>
                                        
                                        <div class="item-type-badge" :class="item.item_type">
                                            {{ item.item_type === 'voucher' ? 'V' : 'B' }}
                                        </div>
                                        
                                        <div class="item-info">
                                            <span class="item-name" :class="{ 'text-decoration-line-through text-muted': item.willBeRemoved }">
                                                {{ item.item_name || item.name }}
                                            </span>
                                            <span class="item-type-label">
                                                {{ item.item_type }}
                                                <span v-if="item.willBeRemoved && isEditingSettings" class="text-danger ms-1 small">
                                                    (Will be removed)
                                                </span>
                                            </span>
                                        </div>
                                        
                                        <button 
                                            v-if="!isEditingSettings && write"
                                            class="btn btn-sm text-danger" 
                                            @click.stop="removeItem(item.id)"
                                            title="Remove item"
                                        >
                                            <i class="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </transition-group>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State / Onboarding -->
                <div v-else-if="page.output && !hasSettings" class="onboarding-container">
                    <div class="onboarding-card-wrapper">
                        <div class="onboarding-card">
                            <div class="onboarding-visual">
                                <div class="onboarding-icon">
                                    <i class="fas fa-rocket"></i>
                                </div>
                                <div class="decoration decoration-1"></div>
                                <div class="decoration decoration-2"></div>
                            </div>
                            
                            <h2 class="onboarding-title">Boost Member Engagement</h2>
                            <p class="onboarding-description">
                                Create a custom "Promotions" section to highlight selected vouchers and benefits directly on the member dashboard.
                            </p>

                            <div class="onboarding-features">
                                <div class="feature-item">
                                    <div class="feature-icon"><i class="fas fa-bullseye"></i></div>
                                    <div class="feature-text">
                                        <strong>Curated Visibility</strong>
                                        <span>Pick specifically what you want members to notice first.</span>
                                    </div>
                                </div>
                                <div class="feature-item">
                                    <div class="feature-icon"><i class="fas fa-layer-group"></i></div>
                                    <div class="feature-text">
                                        <strong>Flexible Content</strong>
                                        <span>Display vouchers, benefits, or a mix of both seamlessly.</span>
                                    </div>
                                </div>
                                <div class="feature-item">
                                    <div class="feature-icon"><i class="fas fa-clock"></i></div>
                                    <div class="feature-text">
                                        <strong>Timed Offers</strong>
                                        <span>Highlight limited-time deals to create urgency and action.</span>
                                    </div>
                                </div>
                            </div>

                            <button class="btn-onboarding" @click="createInitialSettings" v-if="write">
                                <span>Get Started & Setup Section</span>
                                <i class="fas fa-arrow-right ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-else class="d-flex justify-content-center py-5">
                    <div class="spinner-border text-primary"></div>
                </div>
            </div>
            <!-- Right Side: Mobile Preview -->
            <div class="col-lg-5 d-none d-lg-block">
                <div class="sticky-top" style="top: 20px; z-index: 10;">
                    <div class="promotions-card preview-card shadow-sm">
                        <div class="promotions-header">
                            <div class="header-left">
                                <h4 class="header-title">
                                    <i class="fas fa-mobile-alt me-2 primary-text"></i>Live Preview
                                </h4>
                                <p class="header-subtitle">Real-time mobile display reflection</p>
                            </div>
                        </div>
                        <div class="promotions-content preview-inner bg-white">
                            <mobile-preview 
                                :items="previewItems" 
                                :title="activeSettings.section_title" 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div v-if="!thisComponentShow" class="mt-5">
            <o-warning-box
                title="Permission Denied"
                message="You do not have required permission for promotions"
                action-text="Go to Home"
                cancel-text="Back"
                :hide-cancel="true"
                @action="$router.push({ name: 'Home' })"
            />
        </div>

        <!-- Global Warning Box Modal -->
        <o-warning-box
            v-if="warningBox.show"
            :title="warningBox.title"
            :message="warningBox.message"
            :action-text="warningBox.actionText"
            :cancel-text="warningBox.cancelText"
            @action="handleWarningAction"
            @cancel="warningBox.show = false"
        />

        <!-- Item Selection Modal -->
        <div v-if="showItemModal" class="modal-overlay" @click.self="showItemModal = false">
            <div class="modal-container">
                <div class="modal-header-custom">
                    <h5>Add Items to Promotion</h5>
                    <button class="btn-close" @click="showItemModal = false"></button>
                </div>
                <div class="modal-body-custom">
                    <div v-if="loadingAvailable" class="text-center py-4">
                        <div class="spinner-border text-primary"></div>
                        <p class="mt-2">Loading available items...</p>
                    </div>
                        <div v-else>
                            <div class="mb-3">
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    v-model="itemSearch"
                                    placeholder="Search items..."
                                >
                            </div>
                            <!-- Available Items -->
                            <div v-if="availableItemsFiltered.length === 0 && unavailableItemsFiltered.length === 0" class="text-center py-4">
                                <p class="text-muted">No available items to add</p>
                            </div>
                            <div v-else class="available-items-list">
                                <!-- Available Items Section -->
                                <div 
                                    v-for="item in availableItemsFiltered" 
                                    :key="`${item.type}:${item.id}`"
                                    class="available-item"
                                    :class="{ selected: selectedItems.includes(`${item.type}:${item.id}`) }"
                                    @click="toggleItemSelection(item)"
                                >
                                    <div class="item-checkbox">
                                        <i :class="selectedItems.includes(`${item.type}:${item.id}`) ? 'fas fa-check-square' : 'far fa-square'"></i>
                                    </div>
                                    <div class="item-type-badge" :class="item.type">
                                        {{ item.type === 'voucher' ? 'V' : 'B' }}
                                    </div>
                                    <div class="item-details flex-grow-1">
                                        <div class="d-flex justify-content-between align-items-start mb-1">
                                            <span class="item-name fw-semibold">{{ item.name }}</span>
                                            <span v-if="item.point" class="badge bg-primary ms-2">{{ item.point }} pts</span>
                                        </div>
                                        <div class="item-meta-row">
                                            <span class="item-meta">
                                                <i class="fas fa-store me-1"></i>
                                                {{ item.merchant?.name || 'N/A' }}
                                            </span>
                                        </div>
                                        <div v-if="item.type === 'voucher'" class="item-meta-row mt-1">
                                            <span class="item-meta me-3">
                                                <i class="fas fa-box me-1"></i>
                                                Stock: <strong>{{ (item.is_limited === 0 || item.stock === -1 || item.stock === null) ? 'Unlimited' : (item.stock !== undefined ? item.stock : 'N/A') }}</strong>
                                            </span>
                                            <span class="item-meta">
                                                <i class="fas fa-calendar-alt me-1"></i>
                                                Expiration: {{ formatDate(item.expiration) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Divider for Unavailable Items -->
                                <div v-if="unavailableItemsFiltered.length > 0" class="unavailable-divider">
                                    <span>Out of Stock / Expired Vouchers</span>
                                </div>

                                <!-- Unavailable Items Section -->
                                <div 
                                    v-for="item in unavailableItemsFiltered" 
                                    :key="`unavailable-${item.type}:${item.id}`"
                                    class="available-item unavailable-item"
                                >
                                    <div class="item-checkbox">
                                        <i class="fas fa-ban text-muted"></i>
                                    </div>
                                    <div class="item-type-badge voucher opacity-50">
                                        V
                                    </div>
                                    <div class="item-details flex-grow-1">
                                        <div class="d-flex justify-content-between align-items-start mb-1">
                                            <span class="item-name fw-semibold text-muted">{{ item.name }}</span>
                                            <span v-if="item.point" class="badge badge-point-muted ms-2">{{ item.point }} pts</span>
                                        </div>
                                        <div class="item-meta-row">
                                            <span class="item-meta">
                                                <i class="fas fa-store me-1"></i>
                                                {{ item.merchant?.name || 'N/A' }}
                                            </span>
                                        </div>
                                        <div class="item-meta-row mt-1">
                                            <span class="item-meta me-3" :class="{ 'text-danger': item.stock === 0 }">
                                                <i class="fas fa-box me-1"></i>
                                                Stock: <strong>{{ (item.is_limited === 0 || item.stock === -1 || item.stock === null) ? 'Limited' : (item.stock !== undefined ? item.stock : 'N/A') }}</strong>
                                            </span>
                                            <span class="item-meta" :class="{ 'text-danger': isExpired(item.expiration) }">
                                                <i class="fas fa-calendar-alt me-1"></i>
                                                Expiration: {{ formatDate(item.expiration) }}
                                                <span v-if="isExpired(item.expiration)" class="ms-1">(Expired)</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                </div>
                <div class="modal-footer-custom justify-content-end">
                    <button class="btn custom-cancel-btn" @click="showItemModal = false">
                        Cancel
                    </button>
                    <button 
                        class="btn custom-add-btn" 
                        @click="addSelectedItems"
                        :disabled="page.isSaving"
                    >
                        <span v-if="page.isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ page.isSaving ? 'Adding...' : 'Add Selected' }}
                    </button>
                </div>
            </div>
        </div>
        </FeatureGate>
    </div>
</template>


<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { OBreadCrumb, OWarningBox } from '@/components/OmnihControl'
import FeatureGate from '@/components/upsell/FeatureGate.vue'
import MobilePreview from './MobilePreview.vue'

import ToastNotification from '@/components/member/ToastNotification.vue'
import { usePointSystem } from '@/composables/pointsystem'

export default {
    name: 'Promotions',
    components: {
        OBreadCrumb,
        FeatureGate,
        MobilePreview,
        ToastNotification,
        OWarningBox
    },
    setup() {
        const store = useStore()
        const { isPointSystemActive } = usePointSystem()

        const isBenefitModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.benefitModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const isPromotionsModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.promotionsModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const toast = reactive({
            show: false,
            message: '',
            type: 'success',
            title: ''
        })

        const warningBox = reactive({
            show: false,
            title: 'Warning',
            message: '',
            actionText: 'Proceed',
            cancelText: 'Cancel',
            action: null
        })

        function triggerToast(message, type = 'success', title = '') {
            toast.message = message
            toast.type = type
            toast.title = title
            toast.show = true
        }

        function triggerWarning(title, message, action, actionText = 'Proceed', cancelText = 'Cancel') {
            warningBox.title = title
            warningBox.message = message
            warningBox.action = action
            warningBox.actionText = actionText
            warningBox.cancelText = cancelText
            warningBox.show = true
        }

        function handleWarningAction() {
            if (warningBox.action) warningBox.action()
            warningBox.show = false
        }

        const page = reactive({
            isLoading: false,
            isSaving: false,
            output: false,
            component: 'promotions'
        })

        const write = ref(false)
        const thisComponentShow = ref(true)

        const showItemModal = ref(false)
        const loadingAvailable = ref(false)
        const itemSearch = ref('')
        const selectedItems = ref([]) // Format: ["type:id", ...]
        const draggedIndex = ref(null)
        const isEditingSettings = ref(false)
        const itemsToRemove = ref([]) // List of IDs to remove when savinged
        const showCreateForm = ref(false)
        const isOrderChanged = ref(false)

        // Editable settings for edit mode
        const editableSettings = reactive({
            section_title: 'Limited offers',
            display_limit: 5,
            content_type: 'BENEFIT'
        })

        // Check if corporate has point plan (for voucher selection)
        // Initialize from composable value directly
        const canSelectVoucher = ref(isPointSystemActive.value)

        const hasSettings = computed(() => {
            return store.state.Promotion.settings.length > 0
        })

        const currentSetting = computed(() => {
            return store.state.Promotion.currentSetting || store.state.Promotion.settings[0]
        })

        const items = computed(() => {
            let list = store.state.Promotion.items || []
            if (!isBenefitModuleEnabled.value) {
                list = list.filter(item => item.item_type !== 'benefit')
            }
            if (!isPointSystemActive.value) {
                list = list.filter(item => item.item_type !== 'voucher')
            }
            return list
        })

        const availableItems = computed(() => {
            return store.state.Promotion.availableItems || []
        })

        const filteredAvailableItems = computed(() => {
            let filtered = availableItems.value

            if (!isBenefitModuleEnabled.value) {
                filtered = filtered.filter(item => item.type !== 'benefit')
            }

            // Additional filtering based on editable settings
            if (isEditingSettings.value) {
                if (editableSettings.content_type === 'VOUCHER') {
                    filtered = filtered.filter(item => item.type === 'voucher')
                } else if (editableSettings.content_type === 'BENEFIT') {
                    filtered = filtered.filter(item => item.type === 'benefit')
                }
            } else if (currentSetting.value) {
                 if (currentSetting.value.content_type === 'VOUCHER') {
                    filtered = filtered.filter(item => item.type === 'voucher')
                } else if (currentSetting.value.content_type === 'BENEFIT') {
                    filtered = filtered.filter(item => item.type === 'benefit')
                }
            }

            if (!itemSearch.value) return filtered
            
            const search = itemSearch.value.toLowerCase()
            return filtered.filter(item => 
                item.name.toLowerCase().includes(search)
            )
        })

        // Separate available and unavailable items
        const availableItemsFiltered = computed(() => {
            return filteredAvailableItems.value.filter(item => {
                // Check if voucher
                if (item.type === 'voucher') {
                    const stock = item.stock
                    const expiration = item.expiration ? new Date(item.expiration) : null
                    const now = new Date()
                    
                    // Available if stock > 0 OR null (unlimited) OR -1 AND not expired
                    // explicit 0 is unavailable.
                    const isStockAvailable = stock === null || stock === undefined || stock === -1 || stock > 0
                    
                    return isStockAvailable && (!expiration || expiration > now)
                }
                // Benefits are always available
                return true
            })
        })

        const unavailableItemsFiltered = computed(() => {
            return filteredAvailableItems.value.filter(item => {
                // Only vouchers can be unavailable
                if (item.type === 'voucher') {
                    const stock = item.stock // unlimited might be null
                    const expiration = item.expiration ? new Date(item.expiration) : null
                    const now = new Date()
                    
                    const isStockEmpty = stock !== null && stock !== undefined && stock !== -1 && stock <= 0

                    // Unavailable if stock = 0 OR expired
                    return isStockEmpty || (expiration && expiration <= now)
                }
                return false
            })
        })

        // Computed properties for removal statistics
        const removalStats = computed(() => {
            if (!isEditingSettings.value) {
                return { total: 0, incompatible: 0, manualRemoval: 0, finalCount: items.value.length, isValid: true, needToRemove: 0 }
            }

            // Count incompatible items (auto-removed)
            const incompatibleItems = items.value.filter(item => 
                (editableSettings.content_type === 'VOUCHER' && item.item_type === 'benefit') ||
                (editableSettings.content_type === 'BENEFIT' && item.item_type === 'voucher')
            )
            const incompatible = incompatibleItems.length

            // Count compatible items
            const compatibleItems = items.value.filter(item => 
                !((editableSettings.content_type === 'VOUCHER' && item.item_type === 'benefit') ||
                  (editableSettings.content_type === 'BENEFIT' && item.item_type === 'voucher'))
            )

            // Count manually selected for removal (only from compatible list)
            const manualRemoval = itemsToRemove.value.filter(id => 
                compatibleItems.some(item => item.id === id)
            ).length

            // If we have items marked for removal via UI but not yet in itemsToRemove (e.g. isNew items that were toggled)
            const newItemsRemoved = items.value.filter(item => item.isNew && item.willBeRemoved).length
            
            // Total items that will be gone
            // Note: incompatible items are auto-removed. manualRemoval tracks existing items. newItemsRemoved tracks new items.
            // We need to be careful not to double count if logic overlaps, but currently they are distinct sets.
            
            const total = incompatible + manualRemoval + newItemsRemoved
            const finalCount = items.value.length - total
            
            // Calculate how many more items need to be removed
            const needToRemove = Math.max(0, finalCount - editableSettings.display_limit)
            const isValid = finalCount <= editableSettings.display_limit

            return {
                total,
                incompatible,
                manualRemoval,
                finalCount,
                isValid,
                needToRemove
            }
        })

        // Computed property to determine which items should be marked for removal
        const itemsWithRemovalStatus = computed(() => {
            if (!isEditingSettings.value) {
                return items.value.map(item => ({ ...item, willBeRemoved: false }))
            }

            return items.value.map((item) => {
                let willBeRemoved = false

                // Check if item type doesn't match new content type (always remove, no manual toggle)
                if ((editableSettings.content_type === 'VOUCHER' && item.item_type === 'benefit') ||
                    (editableSettings.content_type === 'BENEFIT' && item.item_type === 'voucher')) {
                    willBeRemoved = true
                } else {
                    // For compatible items, check if manually marked for removal
                    willBeRemoved = itemsToRemove.value.includes(item.id)
                }

                return { ...item, willBeRemoved }
            })
        })

        // Computed: Items to show in Preview (Real-time reflection of edits)
        const previewItems = computed(() => {
            const list = itemsWithRemovalStatus.value
            // Filter out items marked for removal so preview is accurate
            // Exclude items that are 'willBeRemoved'
            return list.filter(item => !item.willBeRemoved)
        })

        const activeSettings = computed(() => {
            return isEditingSettings.value ? editableSettings : (currentSetting.value || { section_title: 'Limited offers' })
        })

        const checkUserPermissions = () => {
            if (store.state.Auth.currUser.level === 'root') {
                write.value = true
                return
            }

            const userModules = store.state.Auth.userInfo.modules
            if (userModules) {
                const matchingModule = userModules.find(module => module.module_name === page.component)
                if (matchingModule) {
                    write.value = matchingModule.write
                } else {
                    write.value = false
                    thisComponentShow.value = false
                }
            } else {
                console.error('userModules is undefined')
                write.value = false
                thisComponentShow.value = false
            }
        }

        onMounted(() => {
            if (store.state.Auth.selfLoaded) {
                checkUserPermissions()
                if (thisComponentShow.value) {
                    loadSettings()
                }
            }
        })

        watch(() => store.state.Auth.selfLoaded, (newVal) => {
            if (newVal) {
                checkUserPermissions()
                if (thisComponentShow.value) {
                    loadSettings()
                }
            }
        })

        // Watch point system changes
        watch(() => isPointSystemActive.value, (newVal) => {
            canSelectVoucher.value = newVal
            
            if (!newVal && editableSettings.content_type !== 'BENEFIT') {
                editableSettings.content_type = 'BENEFIT'
            }
        }, { immediate: true })

        /**
         * Load promotion settings for current corporate
         */
        async function loadSettings() {
            page.isLoading = true

            try {
                await store.dispatch('Promotion/readSettings')
                
                if (hasSettings.value) {
                    const setting = store.state.Promotion.settings[0]
                    store.commit('Promotion/setCurrentSetting', setting)
                    await loadItems(setting.id)
                }
                
                page.output = true
            } catch (err) {
                triggerToast(err.response?.data?.message || 'Failed to load promotion settings', 'error')
                page.output = true
            } finally {
                page.isLoading = false
            }
        }

        /**
         * Load items for a promotion setting
         */
        async function loadItems(settingId) {
            try {
                await store.dispatch('Promotion/readItems', settingId)
            } catch (err) {
                console.error('Failed to load items:', err)
            }
        }

        /**
         * Load available items for selection
         */
        async function loadAvailableItems() {
            if (!currentSetting.value) return
            
            loadingAvailable.value = true
            try {
                await store.dispatch('Promotion/readAvailableItems', currentSetting.value.id)
            } catch (err) {
                console.error('Failed to load available items:', err)
            } finally {
                loadingAvailable.value = false
            }
        }

        /**
         * Create initial promotion settings with default values
         */
        async function createInitialSettings() {
            page.isLoading = true

            try {
                const businessProfile = JSON.parse(localStorage.getItem('selected_business_profile'))
                const data = {
                    corporate_id: businessProfile?.corporate_id,
                    section_title: 'Limited offers',
                    display_limit: 5,
                    content_type: 'BENEFIT'
                }

                await store.dispatch('Promotion/createSetting', data)
                triggerToast('Settings created successfully', 'success')
                await loadSettings()
            } catch (err) {
                triggerToast(err.response?.data?.message || 'Failed to create settings', 'error')
            } finally {
                page.isLoading = false
            }
        }

        /**
         * Toggle item removal - user can freely choose which items to remove
         */
        function toggleItemRemoval(item) {
            if (!isEditingSettings.value) return
            
            // Prevent toggling incompatible items (they must be removed)
            if ((editableSettings.content_type === 'VOUCHER' && item.item_type === 'benefit') ||
                (editableSettings.content_type === 'BENEFIT' && item.item_type === 'voucher')) {
                return
            }

            // For new items (local only)
            if (item.isNew) {
                // Toggle removal status directly on the object
                item.willBeRemoved = !item.willBeRemoved
                
                // Trigger reactivity update for array
                items.value = [...items.value]
                return
            }
            
            // For existing items
            const removeIndex = itemsToRemove.value.indexOf(item.id)
            if (removeIndex > -1) {
                itemsToRemove.value.splice(removeIndex, 1)
            } else {
                itemsToRemove.value.push(item.id)
            }
        }

        /**
         * Start editing settings - populate editable fields
         */
        function startEditSettings() {
            if (currentSetting.value) {
                editableSettings.section_title = currentSetting.value.section_title || 'Limited offers'
                editableSettings.display_limit = currentSetting.value.display_limit || 5
                editableSettings.content_type = currentSetting.value.content_type || 'BENEFIT'
                isEditingSettings.value = true
                
                // Start with empty removal list - user chooses which items to remove
                itemsToRemove.value = []
            }
        }

        /**
         * Save all settings changes and remove marked items
         */
        async function saveSettings() {
            if (!currentSetting.value) return

            // Validate Title and Display Limit
            if (!editableSettings.section_title || editableSettings.section_title.trim() === '') {
                triggerToast('Section Title is required.', 'error')
                return
            }
            if (!editableSettings.display_limit || editableSettings.display_limit < 1 || editableSettings.display_limit > 50) {
                triggerToast('Display Limit must be between 1 and 50.', 'error')
                return
            }

            // Validate final item count doesn't exceed display limit
            const finalItemCount = itemsWithRemovalStatus.value.filter(item => !item.willBeRemoved).length
            if (finalItemCount > editableSettings.display_limit) {
                triggerWarning(
                    'Limit Exceeded',
                    `Cannot save: ${finalItemCount} items will remain but display limit is ${editableSettings.display_limit}. Please select at least ${finalItemCount - editableSettings.display_limit} more item(s) to remove.`,
                    null,
                    'OK',
                    'Close'
                )
                warningBox.action = null // Just an alert
                return
            }

            // Show confirmation if items will be removed
            if (removalStats.value.total > 0) {
                triggerWarning(
                    'Save Changes',
                    `This will remove ${removalStats.value.total} item(s) from the promotion settings. Are you sure you want to proceed?`,
                    async () => {
                        await finalizeSave()
                    },
                    'Yes, Save Changes'
                )
                return
            }

            await finalizeSave()
        }

        async function finalizeSave(exitEditMode = true) {
            page.isSaving = true

            try {
                // CAPTURE CURRENT SORT ORDER (by Product ID)
                // We use Product ID because promotion_item_id might change or not exist for new items
                const sortOrderMap = items.value.reduce((map, item, index) => {
                    // Use item_id (which is product/benefit ID) as key
                    const key = item.item_id || item.id
                    map[key] = index
                    return map
                }, {})

                const businessProfile = JSON.parse(localStorage.getItem('selected_business_profile'))
                const data = {
                    corporate_id: businessProfile?.corporate_id,
                    section_title: editableSettings.section_title,
                    display_limit: editableSettings.display_limit,
                    content_type: editableSettings.content_type
                }


                // Update settings first
                await store.dispatch('Promotion/updateSetting', {
                    id: currentSetting.value.id,
                    data
                })

                // 1. Remove items (Backend)
                const itemsToRemoveList = itemsWithRemovalStatus.value
                    .filter(item => item.willBeRemoved && !item.isNew)
                    .map(item => item.promotion_item_id)

                for (const itemId of itemsToRemoveList) {
                    try {
                        await store.dispatch('Promotion/removeItem', {
                            itemId: itemId,
                            promotionSettingId: currentSetting.value.id
                        })
                    } catch (err) {
                        console.error('Failed to remove item:', err)
                    }
                }

                // 2. Add new pending items (Backend)
                // 2. Add new pending items (Backend)
                // Filter new items to match the NEW content_type to avoid mismatch errors
                const targetContentType = data.content_type || currentSetting.value?.content_type || 'BOTH'
                
                const newItemsToAdd = items.value.filter(item => {
                    if (!item.isNew) return false
                    
                    // If target is SPECIFIC (VOUCHER/BENEFIT), only allow matching types
                    if (targetContentType === 'VOUCHER' && item.item_type !== 'voucher') return false
                    if (targetContentType === 'BENEFIT' && item.item_type !== 'benefit') return false
                    
                    return true
                })

                if (newItemsToAdd.length > 0) {
                   const itemsPayload = newItemsToAdd.map(item => ({
                        item_id: item.item_id || item.id, // Ensure correct ID mapping
                        item_type: item.item_type
                   }))
                   
                   try {
                        await store.dispatch('Promotion/addItems', {
                            promotionSettingId: currentSetting.value.id,
                            items: itemsPayload
                        })
                   } catch (err) {
                       console.error('Failed to add new items:', err)
                       throw err
                   }
                }

                // Update current setting in store
                const updatedSetting = { ...currentSetting.value, ...data }
                store.commit('Promotion/setCurrentSetting', updatedSetting)

                // 3. RELOAD ITEMS (to get real promotion_item_ids for new items)
                await loadItems(currentSetting.value.id)

                // 4. RESTORE SORT ORDER & SYNC
                // Map the reloaded backend items to the user's desired order
                const backendItems = [...store.state.Promotion.items]
                
                backendItems.sort((a, b) => {
                    const indexA = sortOrderMap[a.item_id] ?? 999
                    const indexB = sortOrderMap[b.item_id] ?? 999
                    return indexA - indexB
                })

                // Construct payload for order update
                const reorderedItems = backendItems.map((item, idx) => ({
                    promotion_item_id: item.promotion_item_id,
                    sort_order: idx + 1
                }))

                // Send Sort Update (only if there are items)
                if (reorderedItems.length > 0) {
                    await store.dispatch('Promotion/updateItemsOrder', reorderedItems)
                }
                
                // Final local update to match
                const finalItems = backendItems.map((item, idx) => ({
                    ...item,
                    sort_order: idx + 1
                }))
                store.commit('Promotion/setItems', finalItems)


                const removedCount = itemsToRemoveList.length
                const addedCount = newItemsToAdd.length
                
                let successMsg = 'Settings updated successfully.'
                if (removedCount > 0) successMsg += ` ${removedCount} item(s) removed.`
                if (addedCount > 0) successMsg += ` ${addedCount} item(s) added.`

                triggerToast(successMsg, 'success')
                
                if (exitEditMode) {
                    isEditingSettings.value = false
                    itemsToRemove.value = []
                    isOrderChanged.value = false
                }

            } catch (err) {
                triggerToast(err.response?.data?.message || 'Failed to update settings', 'error')
            } finally {
                page.isSaving = false
            }
        }

        /**
         * Cancel editing and revert changes
         */
        function cancelEditSettings() {
            // Revert to original values
            if (currentSetting.value) {
                editableSettings.section_title = currentSetting.value.section_title || 'Limited offers'
                editableSettings.display_limit = currentSetting.value.display_limit || 5
                editableSettings.content_type = currentSetting.value.content_type || 'BENEFIT'

                // Reload items to discard local changes (added items, etc.)
                loadItems(currentSetting.value.id)
            }
            isEditingSettings.value = false
            itemsToRemove.value = []
            isOrderChanged.value = false
        }

        /**
         * Handle opening item modal with consistency check
         */
         function handleOpenItemModal() {
            // Validate Display Limit
            if (isEditingSettings.value && (!editableSettings.display_limit || editableSettings.display_limit < 1)) {
                 triggerToast('Please set a valid Display Limit first (1-50).', 'warning')
                 return
            }

            // If content type changed, we MUST save to backend first so it knows what items to return
            if (isEditingSettings.value && editableSettings.content_type !== currentSetting.value.content_type) {
                triggerWarning(
                    'Save Required',
                    'Changing Content Type requires saving settings to load compatible items. Save changes now?',
                    async () => {
                        await finalizeSave(false) // false = keep editing
                        showItemModal.value = true
                        loadAvailableItems()
                    },
                    'Save & Continue'
                )
                return
            }
            
            showItemModal.value = true
        }

        /**
         * Toggle item selection in modal
         */
        function toggleItemSelection(item) {
            const key = `${item.type}:${item.id}`
            const index = selectedItems.value.indexOf(key)
            
            // Prevent selection of unavailable items (stock 0 or expired vouchers)
            if (item.type === 'voucher') {
                const stock = item.stock // dont default to 0 yet
                const expiration = item.expiration ? new Date(item.expiration) : null
                const now = new Date()
                
                const isStockEmpty = stock !== null && stock !== undefined && stock !== -1 && stock <= 0
                
                // Unavailable if stock is explicitly 0 (and not null/-1) OR expired
                if (isStockEmpty || (expiration && expiration <= now)) {
                    triggerToast('Cannot select unavailable voucher (stock 0 or expired)', 'warning')
                    return
                }
            }
            
            if (index > -1) {
                selectedItems.value.splice(index, 1)
            } else {
                // Check if adding would exceed limit
                // IMPORTANT: Calculate current count based on FINAL count (after potential removals), not raw length
                const currentFinalCount = itemsWithRemovalStatus.value.filter(item => !item.willBeRemoved).length
                
                // Backend limit is 50. Use 50 as hard limit for items list if needed, or stick to display_limit.
                // Request says "Validasi semua max itemsnya jadi sesuai dengan backend aja, diakrenakan di backend dia handle 50"
                // Assuming display_limit is THE limit.
                
                const maxLimit = isEditingSettings.value 
                    ? (editableSettings.display_limit || 50) 
                    : (currentSetting.value?.display_limit || 50)
                
                // Ensure maxLimit considers the 50 hard cap if display_limit is somehow higher or undefined
                const absoluteMax = 50; 
                const effectiveLimit = Math.min(maxLimit, absoluteMax);

                if (currentFinalCount + selectedItems.value.length >= effectiveLimit) {
                    triggerToast(`Cannot add more items. Limit is ${effectiveLimit}. (Final count: ${currentFinalCount})`, 'warning')
                    return
                }
                selectedItems.value.push(key)
            }
        }

        /**
         * Add selected items to promotion
         */
        async function addSelectedItems() {
            if (selectedItems.value.length === 0) {
                triggerToast('Please select at least 1 item to add.', 'warning')
                return
            }

            // If editing settings, add to local state only (pending save)
            if (isEditingSettings.value) {
                const newItems = []
                
                selectedItems.value.forEach(key => {
                    const [type, id] = key.split(':')
                    // Find full item details from availableItems
                    const fullItem = availableItems.value.find(i => i.id === id && i.type === type)
                    
                    if (fullItem) {
                        newItems.push({
                            ...fullItem,
                            id: fullItem.id, // Use original ID temporarily
                            item_id: fullItem.id,
                            item_name: fullItem.name,
                            item_type: type,
                            isNew: true, // Flag as new
                            willBeRemoved: false
                        })
                    }
                })

                // Add to Vuex store state locally
                const currentItems = [...items.value]
                store.commit('Promotion/setItems', [...currentItems, ...newItems])
                
                triggerToast('Items staged for addition. Click "Save Changes" to apply.', 'info')
                showItemModal.value = false
                selectedItems.value = []
                return
            }

            // Normal mode: Add immediately via API
            page.isSaving = true

            try {
                const itemsToAdd = selectedItems.value.map(key => {
                    const [type, id] = key.split(':') // Split by colon instead of dash
                    return { item_id: id, item_type: type }
                })

                await store.dispatch('Promotion/addItems', {
                    promotionSettingId: currentSetting.value.id,
                    items: itemsToAdd
                })

                // Reload items to get the updated list with new items
                await loadItems(currentSetting.value.id)
                
                // Reload available items to update the modal
                await loadAvailableItems()

                triggerToast('Items added successfully', 'success')
                showItemModal.value = false
                selectedItems.value = []
            } catch (err) {
                triggerToast(err.response?.data?.message || 'Failed to add items', 'error')
            } finally {
                page.isSaving = false
            }
        }

        /**
         * Remove item from promotion
         */
        async function removeItem(itemId) {
            triggerWarning(
                'Remove Item',
                'Are you sure you want to remove this item from the promotion? This action cannot be undone.',
                async () => {
                    // Check if it's a new item (local only)
                    const item = items.value.find(i => i.id === itemId)
                    if (isEditingSettings.value && item?.isNew) {
                         const updatedItems = items.value.filter(i => i.id !== itemId)
                         store.commit('Promotion/setItems', updatedItems)
                         triggerToast('Item removed from pending list', 'success')
                         return
                    }

                    page.isSaving = true
                    try {
                        if (!item) {
                            triggerToast('Item not found', 'error')
                            return
                        }

                        await store.dispatch('Promotion/removeItem', {
                            itemId: item.promotion_item_id,
                            promotionSettingId: currentSetting.value.id
                        })
                        
                        const updatedItems = items.value.filter(i => i.id !== itemId)
                        store.commit('Promotion/setItems', updatedItems)
                        
                        triggerToast('Item removed successfully', 'success')
                    } catch (err) {
                        triggerToast(err.response?.data?.message || 'Failed to remove item', 'error')
                    } finally {
                        page.isSaving = false
                    }
                },
                'Yes, Remove It'
            )
        }

        /**
         * Drag and drop handlers for reordering
         */
        let dragThrottle = false;
        let startIndex = null; // Track starting position

        function dragStart(index, event) {
            startIndex = index
            draggedIndex.value = index
            if (event) {
                // Stabilize drag ghost
                const target = event.currentTarget
                event.dataTransfer.effectAllowed = 'move'
                event.dataTransfer.dropEffect = 'move'
                
                // Set the ghost look
                target.classList.add('is-drag-snapshot')
                event.dataTransfer.setDragImage(target, 20, 30)
                
                // Use a tiny delay to ensure the browser captures the snapshot before we hide the original
                requestAnimationFrame(() => {
                    target.classList.remove('is-drag-snapshot')
                    target.classList.add('is-placeholder')
                })
            }
        }

        function dragEnd() {
            // Check if item ended up in a different position
            if (startIndex !== null && draggedIndex.value !== null && startIndex !== draggedIndex.value) {
                isOrderChanged.value = true
            }

            draggedIndex.value = null
            startIndex = null
            dragThrottle = false
            const placeholders = document.querySelectorAll('.is-placeholder')
            placeholders.forEach(el => el.classList.remove('is-placeholder'))
        }

        function dragOver(index, event) {
            // Prevent self-triggering and rapid swapping
            if (draggedIndex.value === null || draggedIndex.value === index || dragThrottle) return

            // Midpoint detection: Only swap if mouse is past the halfway point of the target
            const targetRect = event.currentTarget.getBoundingClientRect();
            const midpoint = targetRect.top + targetRect.height / 2;
            
            // If dragging down, must be below midpoint. If dragging up, must be above midpoint.
            if (draggedIndex.value < index && event.clientY < midpoint) return;
            if (draggedIndex.value > index && event.clientY > midpoint) return;

            dragThrottle = true
            setTimeout(() => { dragThrottle = false }, 50) 

            const itemsCopy = [...items.value]
            const [draggedItem] = itemsCopy.splice(draggedIndex.value, 1)
            itemsCopy.splice(index, 0, draggedItem)
            
            const updatedItemsWithNewOrder = itemsCopy.map((item, idx) => ({
                ...item,
                sort_order: idx + 1
            }))
            
            store.commit('Promotion/setItems', updatedItemsWithNewOrder)
            draggedIndex.value = index 
        }

        async function drop() {
            // Drop logic largely handled by dragOver reactivity now
            // Final cleanup happens in dragEnd which is always called
        }

        async function saveOrder() {
            page.isSaving = true
            const updatedItems = items.value.map((item, idx) => ({
                promotion_item_id: item.promotion_item_id,
                sort_order: idx + 1
            }))

            try {
                await store.dispatch('Promotion/updateItemsOrder', updatedItems)
                triggerToast('Order updated successfully', 'success')
                isOrderChanged.value = false
                
                // Final sync from backend potentially
                await loadItems(currentSetting.value.id)
            } catch (err) {
                triggerToast('Failed to update order', 'error')
                await loadItems(currentSetting.value.id)
            } finally {
                page.isSaving = false
            }
        }

        function cancelOrderChange() {
            isOrderChanged.value = false
            loadItems(currentSetting.value.id)
        }

        /**
         * Format content type for display
         */
        function formatContentType(type) {
            const types = {
                'VOUCHER': 'Vouchers Only',
                'BENEFIT': 'Benefits Only',
                'BOTH': 'Both (Mixed)'
            }
            return types[type] || type
        }

        /**
         * Get badge class for content type
         */
        function getContentTypeBadgeClass(type) {
            const classes = {
                'VOUCHER': 'badge bg-primary',
                'BENEFIT': 'badge bg-success',
                'BOTH': 'badge bg-info'
            }
            return classes[type] || 'badge bg-secondary'
        }

        /**
         * Format date for display
         */
        function formatDate(dateString) {
            if (!dateString) return 'N/A'
            const date = new Date(dateString)
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        }

        /**
         * Check if date is expired
         */
        function isExpired(dateString) {
            if (!dateString) return false
            const date = new Date(dateString)
            const now = new Date()
            return date <= now
        }

        // Watch for modal open to load available items
        watch(showItemModal, (newVal) => {
            if (newVal) {
                selectedItems.value = []
                itemSearch.value = ''
                loadAvailableItems()
            }
        })

        // Watch for settings changes to provide real-time feedback
        watch(() => editableSettings.content_type, (newType) => {
            if (isEditingSettings.value) {
                // Remove items from itemsToRemove that are now incompatible (they will be auto-removed)
                const incompatibleIds = items.value
                    .filter(item => (newType === 'VOUCHER' && item.item_type === 'benefit') ||
                                   (newType === 'BENEFIT' && item.item_type === 'voucher'))
                    .map(item => item.id)
                
                itemsToRemove.value = itemsToRemove.value.filter(id => !incompatibleIds.includes(id))
            }
        })

        watch(() => editableSettings.display_limit, () => {
            if (isEditingSettings.value) {
                // Keep current itemsToRemove selections - user decides which to remove
            }
        })

        watch(() => editableSettings.section_title, () => {
            if (isEditingSettings.value) {
                // Real-time title update handled via reactivity
            }
        })

        function onlyNumbers(evt) {
             const charCode = (evt.which) ? evt.which : evt.keyCode
             if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
                 evt.preventDefault()
             }
             return true
        }

        function checkDisplayLimit() {
            if (editableSettings.display_limit > 50) {
                editableSettings.display_limit = 50
            }
        }

        return {
            onlyNumbers,
            checkDisplayLimit,
            toast,
            triggerToast,
            warningBox,
            triggerWarning,
            handleWarningAction,
            page,
            showItemModal,
            loadingAvailable,
            itemSearch,
            selectedItems,
            isEditingSettings,
            editableSettings,
            itemsToRemove,
            canSelectVoucher,
            showCreateForm,
            hasSettings,
            currentSetting,
            items,
            itemsWithRemovalStatus,
            availableItems,
            filteredAvailableItems,
            availableItemsFiltered,
            unavailableItemsFiltered,
            removalStats,
            previewItems,
            activeSettings,
            createInitialSettings,
            startEditSettings,
            saveSettings,
            cancelEditSettings,
            toggleItemRemoval,
            toggleItemSelection,
            addSelectedItems,
            handleOpenItemModal,
            removeItem,
            dragStart,
            dragEnd,
            dragOver,
            drop,
            isOrderChanged,
            saveOrder,
            cancelOrderChange,
            formatContentType,
            getContentTypeBadgeClass,
            formatDate,
            thisComponentShow,
            write,
            isExpired,
            isBenefitModuleEnabled,
            isPromotionsModuleEnabled
        }
    }
}
</script>


<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Bootstrap Overrides */
.bg-primary {
    background-color: var(--primary-color, #F93E3E) !important;
    color: white !important;
}

.badge-point-muted {
    background-color: var(--secondary-color, #FEE9E9);
    color: var(--primary-color, #F93E3E);
}

/* Onboarding */
.onboarding-container {
    display: flex;
    justify-content: center;
    align-items: stretch; /* Make the wrapper/card fill the height */
    height: 100%;
}

.onboarding-card-wrapper {
    width: 100%;
    height: 100%;
}

.onboarding-card {
    background: #fff;
    border-radius: 12px;
    padding: 6rem 2.5rem 4rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06); /* shadow-sm */
    border: 1px solid #e5e7eb;
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-align: center;
    width: 100%;
    height: 100%;
    min-height: 800px; /* Standardize with preview-card height */
    display: flex;
    flex-direction: column;
    justify-content: flex-start; /* Shift content towards the top */
}

.onboarding-visual {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto 2rem;
}

.onboarding-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, var(--primary-color, #F93B3B) 0%, #ff6b6b 100%);
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    position: relative;
    z-index: 2;
    transform: rotate(-5deg);
    box-shadow: 0 10px 20px rgba(249, 59, 59, 0.2);
}

.decoration {
    position: absolute;
    background: rgba(249, 59, 59, 0.05);
    border-radius: 50%;
    z-index: 1;
}

.decoration-1 {
    width: 150px;
    height: 150px;
    top: -50px;
    right: -50px;
}

.decoration-2 {
    width: 100px;
    height: 100px;
    bottom: -30px;
    left: -30px;
}

.onboarding-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 1rem;
    letter-spacing: -0.5px;
}

.onboarding-description {
    color: #666;
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 2.5rem;
}

.onboarding-features {
    text-align: left;
    margin-bottom: 3rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.feature-item {
    display: flex;
    gap: 1.25rem;
}

.feature-icon {
    width: 44px;
    height: 44px;
    min-width: 44px;
    background: var(--secondary-color, #FEE9E9);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #F93B3B);
    font-size: 18px;
}

.feature-text {
    display: flex;
    flex-direction: column;
}

.feature-text strong {
    color: #1a1a1a;
    font-size: 15px;
    margin-bottom: 2px;
}

.feature-text span {
    color: #777;
    font-size: 13px;
    line-height: 1.4;
}

.btn-onboarding {
    width: 100%;
    padding: 1.25rem;
    background: #1a1a1a;
    color: white;
    border: none;
    border-radius: 16px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-onboarding:hover {
    background: #000;
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
}

.btn-onboarding:active {
    transform: translateY(0);
}

/* Content */
.promotions-content {
    padding: 1.5rem 2rem;
}

/* Container */
.promotions-container {
    padding: 16px;
    padding-right: 20px;
    max-width: 100%;
}

/* Main Card (Connection Apps Style) */
.promotions-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    height: 100%;
}

/* Header */
.promotions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
}

.header-left {
    flex: 1;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 0.75rem;
}

/* Section Styling */
.settings-section {
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #f3f4f6;
}

.section-label {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    margin-bottom: 0.5rem;
    letter-spacing: 0.05em;
}

/* Form Settings */
.form-control:focus,
.form-select:focus {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 0.2rem var(--secondary-color, #FEE9E9);
}

.form-control,
.form-select {
    transition: all 0.2s ease;
}

/* Buttons */
.section-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: #111827;
}

.alert-warning-custom {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 12px;
    padding: 1rem;
}

.warning-icon-circle {
    width: 40px;
    height: 40px;
    background: #fef3c7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d97706;
}

/* Preview Card Specifics */
.preview-card {
    background: #f8fafc; /* Slightly different background to differentiate from settings */
    height: auto;
    min-height: 800px;
}

.preview-inner {
    display: flex;
    justify-content: center;
    padding: 2rem 1rem !important;
}

.btn-primary-action {
    background: var(--primary-color, #F93E3E);
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
}

.btn-primary-action:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
    color: white !important;
}

.btn-primary-action i {
    color: white !important;
}

.btn-primary-action:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

.btn-secondary-action {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
}

.btn-secondary-action:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Items List */
.items-list-container {
    background: #f9fafb;
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid #f3f4f6;
    margin-top: 1rem;
}

.items-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.item-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: white;
    border-radius: 8px;
    cursor: grab;
    transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;
    position: relative;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    user-select: none;
}

.item-row:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
    border-color: var(--primary-color, #F93E3E);
}

/* Premium Drag Ghost (The thing you hold) */
.item-row.is-drag-snapshot {
    background: white !important;
    box-shadow: 0 15px 35px rgba(0,0,0,0.15), 0 5px 15px rgba(0,0,0,0.1) !important;
    border: 1px solid var(--primary-color, #F93E3E) !important;
    opacity: 1 !important;
    transform: rotate(1.5deg) scale(1.02) !important;
    cursor: grabbing !important;
}

/* Placeholder (The spot left in the list) */
.item-row.is-placeholder {
    opacity: 0.1 !important;
    background: var(--secondary-color, #FEE9E9) !important;
    border: 2px dashed var(--primary-color, #F93E3E) !important;
    box-shadow: none !important;
    transform: scale(0.98) !important;
    pointer-events: none; /* CRITICAL: prevents flicker when dragging over self */
}

/* Transition Group Animations - Smoothed out */
.list-move {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Ensure placeholder doesn't transition too slowly to prevent overlap glitches */
.item-row.is-placeholder {
    transition: none !important;
}

.list-enter-active,
.list-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.item-row:active {
    cursor: grabbing;
}

.item-row:active {
    cursor: grabbing;
}

/* Items marked for removal */
.item-row.item-will-remove {
    background: #fef2f2;
    border: 1px solid #fecaca;
}

.item-row.item-will-remove:hover {
    background: #fee2e2;
    cursor: pointer;
}

/* Items that can be toggled */
.item-row.item-can-toggle {
    cursor: pointer;
}

.item-row.item-can-toggle:hover {
    background: var(--secondary-color, #FEE9E9);
    border-color: var(--primary-color, #F93E3E);
}

/* Shake animation for items that need attention */
.item-row.item-shake {
    animation: shake 0.5s ease-in-out infinite;
    border: 2px solid #f59e0b;
    background: #fffbeb;
}

.item-row.item-shake:hover {
    animation: none;
    background: #fef3c7;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-2px); }
    75% { transform: translateX(2px); }
}

.item-drag-handle {
    color: #d1d5db;
    cursor: grab;
}

.item-removal-indicator {
    color: #ef4444;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.item-keep-indicator {
    color: #10b981;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.item-order {
    width: 24px;
    height: 24px;
    background: #f3f4f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
}

.item-will-remove .item-order {
    background: #fca5a5;
    color: #7f1d1d;
}

.item-type-badge {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: #fff;
}

.item-type-badge.voucher {
    background: var(--primary-color, #F93E3E);
}

.item-type-badge.benefit {
    background: #10b981;
}

.item-will-remove .item-type-badge {
    opacity: 0.5;
}

.item-info {
    flex: 1;
    display: flex;
    flex-direction: column;
}

/* Make item-info clickable when in edit mode to toggle removal */
.item-row.item-can-toggle .item-info {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

.item-row.item-can-toggle .item-info:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.item-name {
    font-weight: 500;
    color: #111827;
    transition: all 0.2s ease;
}

.item-name.text-strikethrough {
    text-decoration: line-through;
    color: #6b7280;
}

.text-strikethrough {
    text-decoration: line-through;
}

.item-type-label {
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: capitalize;
}

.item-toggle-hint {
    font-size: 0.75rem;
    color: #6b7280;
    text-align: center;
    min-width: 60px;
}

/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    backdrop-filter: blur(2px);
}

.modal-container {
    background: #fff;
    border-radius: 12px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.modal-header-custom h5 {
    margin: 0;
    font-weight: 600;
    color: #111827;
}

.modal-body-custom {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
}

.modal-footer-custom {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

.custom-cancel-btn {
    background: #fff;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-weight: 500;
}

.custom-cancel-btn:hover {
    background: #f3f4f6;
    color: #374151;
}

.custom-add-btn {
    background: var(--primary-color, #F93E3E); /* Rose-500 matching the uploaded image tone */
    color: white !important;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-weight: 500;
}

.custom-add-btn:hover {
    background: var(--primary-color, #F93E3E);
    color: white !important;
}

/* Available Items */
.available-items-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 400px;
    overflow-y: auto;
}

.available-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.available-item:hover {
    background: #f3f4f6;
}

.available-item.selected {
    background: var(--secondary-color, #FEE9E9);
    border-color: var(--primary-color, #F93E3E);
}

.item-checkbox {
    font-size: 1.25rem;
    color: #9ca3af;
}

.available-item.selected .item-checkbox {
    color: var(--primary-color, #F93E3E);
}

.item-details {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.item-name {
    font-size: 0.875rem;
    color: #111827;
    line-height: 1.4;
}

.item-meta-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.item-meta {
    font-size: 0.75rem;
    color: #6b7280;
    display: inline-flex;
    align-items: center;
}

/* Unavailable Items */
.unavailable-item {
    opacity: 0.6;
    cursor: not-allowed !important;
    background: #f9fafb !important;
    border-color: #e5e7eb !important;
}

.unavailable-item:hover {
    background: #f9fafb !important;
    transform: none !important;
}

.unavailable-divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 1.5rem 0;
    position: relative;
}

.unavailable-divider::before,
.unavailable-divider::after {
    content: '';
    flex: 1;
    border-bottom: 2px dashed #d1d5db;
}

.unavailable-divider span {
    padding: 0 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

/* Responsive */
@media (max-width: 768px) {
    .onboarding-card {
        padding: 2rem 1.5rem;
    }
    
    .modal-container {
        width: 95%;
        max-height: 90vh;
    }
    
    .header-actions {
        flex-direction: column;
    }
}
</style>

